import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listCalendarEntries } from '../api/calendar.js';
import type {
  CalendarDaySummary,
  CalendarEntry,
  MonthCell,
} from '../api/calendarTypes.js';
import {
  addDays,
  entriesByDate,
  monthWindowOf,
  parseIsoDate,
  shiftMonth,
  summarizeDay,
  todayIso,
} from '../views/calendar/calendarUtils.js';

const API_RANGE_DAYS = 31;

export interface UseCalendarResult {
  loading: boolean;
  error: string | null;
  selectedDate: string;
  monthCells: MonthCell[];
  monthLabel: string;
  entries: CalendarEntry[];
  entriesByDate: Map<string, CalendarEntry[]>;
  summary: CalendarDaySummary;
  setDate: (iso: string) => void;
  goToday: () => void;
  goPrevMonth: () => void;
  goNextMonth: () => void;
  goPrevDay: () => void;
  goNextDay: () => void;
  refresh: () => Promise<void>;
  summaryFor: (iso: string) => CalendarDaySummary;
}

export function useCalendar(): UseCalendarResult {
  const initial = todayIso();
  const [cursor, setCursor] = useState<string>(initial);
  const [selectedDate, setSelectedDate] = useState<string>(initial);
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const windowFor = useCallback((anchor: string) => {
    const w = monthWindowOf(anchor);
    const monthStartIso = w.firstIso;
    const monthEndIso = w.lastIso;
    const monthLen = windowDiffDays(monthStartIso, monthEndIso) + 1;
    if (monthLen <= API_RANGE_DAYS) {
      return { from: monthStartIso, to: monthEndIso, window: w };
    }
    const from = monthStartIso;
    const to = addDays(monthStartIso, API_RANGE_DAYS - 1);
    return { from, to, window: w };
  }, []);

  const [fetchWindow, setFetchWindow] = useState(() => windowFor(initial).from + '|' + windowFor(initial).to);

  const fetchMonth = useCallback(
    async (anchor: string): Promise<void> => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      const { from, to, window } = windowFor(anchor);
      setFetchWindow(`${from}|${to}`);
      setLoading(true);
      setError(null);
      try {
        const rows = await listCalendarEntries({ from, to });
        if (ctrl.signal.aborted) return;
        setEntries(rows);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        const msg = err instanceof Error ? err.message : 'Failed to load';
        setError(msg);
        setEntries([]);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
      void window;
    },
    [windowFor],
  );

  useEffect(() => {
    void fetchMonth(cursor);
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchMonth, cursor]);

  const setDate = useCallback(
    (iso: string): void => {
      setSelectedDate(iso);
      const target = parseIsoMonth(iso);
      const current = parseIsoMonth(cursor);
      if (
        target.year !== current.year ||
        target.month !== current.month
      ) {
        setCursor(iso);
      }
    },
    [cursor],
  );

  const goToday = useCallback((): void => {
    const today = todayIso();
    setSelectedDate(today);
    setCursor(today);
  }, []);

  const goPrevMonth = useCallback((): void => {
    setCursor((c) => shiftMonth(c, -1));
  }, []);

  const goNextMonth = useCallback((): void => {
    setCursor((c) => shiftMonth(c, 1));
  }, []);

  const goPrevDay = useCallback((): void => {
    setSelectedDate((d) => addDays(d, -1));
  }, []);

  const goNextDay = useCallback((): void => {
    setSelectedDate((d) => addDays(d, 1));
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await fetchMonth(cursor);
  }, [fetchMonth, cursor]);

  const window = useMemo(() => monthWindowOf(cursor), [cursor]);

  const monthCells = useMemo<MonthCell[]>(() => {
    const byDate = entriesByDate(entries);
    const fetchFrom = fetchWindow.split('|')[0] ?? '';
    const fetchTo = fetchWindow.split('|')[1] ?? '';
    return window.cells.map((cell) => {
      const present =
        cell.iso >= fetchFrom && cell.iso <= fetchTo;
      return {
        ...cell,
        isSelected: cell.iso === selectedDate,
        entries: present ? byDate.get(cell.iso) ?? [] : [],
      };
    });
  }, [entries, fetchWindow, selectedDate, window.cells]);

  const monthLabel = useMemo(() => {
    return new Intl.DateTimeFormat(undefined, {
      month: 'long',
      year: 'numeric',
    }).format(parseIsoDate(cursor));
  }, [cursor]);

  const byDate = useMemo(() => entriesByDate(entries), [entries]);

  const summary = useMemo(
    () => summarizeDay(entries, selectedDate),
    [entries, selectedDate],
  );

  const summaryFor = useCallback(
    (iso: string): CalendarDaySummary => summarizeDay(entries, iso),
    [entries],
  );

  return {
    loading,
    error,
    selectedDate,
    monthCells,
    monthLabel,
    entries,
    entriesByDate: byDate,
    summary,
    setDate,
    goToday,
    goPrevMonth,
    goNextMonth,
    goPrevDay,
    goNextDay,
    refresh,
    summaryFor,
  };
}

function windowDiffDays(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00Z`).getTime();
  const to = new Date(`${toIso}T00:00:00Z`).getTime();
  return Math.round((to - from) / 86400000);
}

function parseIsoMonth(iso: string): { year: number; month: number } {
  const [y, m] = iso.split('-').map((s) => Number.parseInt(s, 10));
  return { year: y ?? 1970, month: (m ?? 1) - 1 };
}
